import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RegistrarEquiposDialog from "./RegistrarEquiposDialog";
import { formatoEquiposLinea, listarEquiposLinea, mensajeError } from "../services/ordenCompraService";

const cabecera = { fontWeight: 800, fontSize: 11.5, whiteSpace: "nowrap", bgcolor: "action.hover" };
const celda = { py: 0.5, px: 1, fontSize: 12.5 };

// Equipos recibidos con una línea de compra: lista paginada de los ya registrados y acceso a registrar más (Excel o lista pegada).
export default function EquiposLineaDialog({ ordenId, lineaId, onClose, onCambio }) {
  const [formato, setFormato] = useState(null);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [pagina, setPagina] = useState(0);
  const [filas, setFilas] = useState(10);
  const [registrando, setRegistrando] = useState(false);
  const [aviso, setAviso] = useState("");

  const cargarFormato = useCallback(async () => {
    if (!lineaId) return;
    try { const { data } = await formatoEquiposLinea(ordenId, lineaId); setFormato(data); setError(""); } catch (e) { setError(mensajeError(e, "No se pudo cargar la línea.")); }
  }, [ordenId, lineaId]);

  const cargarLista = useCallback(async () => {
    if (!lineaId || !formato?.soportado) return;
    setCargando(true);
    try {
      const { data } = await listarEquiposLinea(ordenId, lineaId, { page: pagina + 1, limit: filas, q: q || undefined });
      setDatos(data);
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los equipos."));
    } finally {
      setCargando(false);
    }
  }, [ordenId, lineaId, formato?.soportado, pagina, filas, q]);

  useEffect(() => { setFormato(null); setDatos(null); setBuscar(""); setQ(""); setPagina(0); setAviso(""); cargarFormato(); }, [lineaId, cargarFormato]);
  useEffect(() => { cargarLista(); }, [cargarLista]);
  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const esModem = formato?.familia === "modem";
  const esVarios = formato?.familia === "varios";
  const total = datos?.total ?? formato?.registrados ?? 0;
  const completo = formato?.soportado && formato.disponibles === 0;

  return (
    <Dialog open={!!lineaId} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography component="span" sx={{ fontSize: 17, fontWeight: 800 }}>Equipos recibidos</Typography>
          {formato?.soportado && <Chip size="small" color={completo ? "success" : "primary"} label={`${formato.registrados} de ${formato.linea.cantidad} registrados`} sx={{ fontWeight: 700 }} />}
        </Box>
        {formato?.linea && <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 400 }}>{formato.linea.detalle}{formato.linea.marca ? ` · ${formato.linea.marca}` : ""}</Typography>}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>{error}</Alert>}
        {aviso && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setAviso("")}>{aviso}</Alert>}
        {!formato && !error && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {formato && !formato.soportado && <Alert severity="info">{formato.motivo}</Alert>}
        {formato?.soportado && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <TextField
                size="small" placeholder="Buscar por serie, ciudad o estado…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: { xs: "100%", sm: 320 } }}
                slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
              />
              {cargando && <CircularProgress size={18} />}
              <Box sx={{ flex: 1 }} />
              {formato.editable && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={completo} onClick={() => setRegistrando(true)} sx={{ textTransform: "none", fontWeight: 700 }}>
                  {completo ? "Línea completa" : "Registrar equipos"}
                </Button>
              )}
            </Box>
            <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {esModem && <TableCell sx={cabecera}>ID</TableCell>}
                    <TableCell sx={cabecera}>{esModem ? "N.º de serie" : "Serie"}</TableCell>
                    {(esModem || esVarios) && <TableCell sx={cabecera}>IMEI / código</TableCell>}
                    <TableCell sx={cabecera}>Ciudad</TableCell>
                    <TableCell sx={cabecera}>Estado</TableCell>
                    <TableCell sx={cabecera}>Registrado</TableCell>
                    <TableCell sx={cabecera}>Por</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!datos?.rows?.length ? (
                    <TableRow><TableCell colSpan={7} sx={{ ...celda, py: 3, textAlign: "center", color: "text.secondary" }}>{cargando ? "Cargando…" : q ? "Ningún equipo coincide." : "Todavía no se registraron equipos de esta línea."}</TableCell></TableRow>
                  ) : datos.rows.map((e) => (
                    <TableRow key={`${e.referencia}-${e.identificador}`} hover>
                      {esModem && <TableCell sx={celda}>{e.referencia}</TableCell>}
                      <TableCell sx={{ ...celda, fontWeight: 700 }}>{e.identificador}</TableCell>
                      {(esModem || esVarios) && <TableCell sx={celda}>{e.emai}</TableCell>}
                      <TableCell sx={celda}>{e.ciudad}</TableCell>
                      <TableCell sx={celda}><Chip size="small" variant="outlined" label={e.estado} sx={{ height: 20, fontSize: 11 }} /></TableCell>
                      <TableCell sx={{ ...celda, whiteSpace: "nowrap" }}>{e.fecha_registro}</TableCell>
                      <TableCell sx={celda}>{e.usu}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div" count={total} page={pagina} rowsPerPage={filas} rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setFilas(Number(e.target.value)); setPagina(0); }}
              labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button></DialogActions>

      <RegistrarEquiposDialog
        open={registrando} ordenId={ordenId} formato={formato} onClose={() => setRegistrando(false)}
        onRegistrados={async ({ creados, omitidos }) => {
          setRegistrando(false);
          setAviso(`${creados} equipo${creados === 1 ? "" : "s"} registrado${creados === 1 ? "" : "s"}${omitidos ? ` · ${omitidos} omitido${omitidos === 1 ? "" : "s"}` : ""}.`);
          setPagina(0);
          await cargarFormato();
          await cargarLista();
          onCambio?.();
        }}
      />
    </Dialog>
  );
}
